<template>
    <div class="chart">
        <div ref="chartdiv" class="chart__area" v-if="!props.isMonthlyPrint"></div>
        <div ref="chartdiv" class="chart__area" v-if="props.isMonthlyPrint" style="width: 50%; height: 63%"></div>
        <div class="chart__wrap" v-if="props.header">
            <div class="chart__label">
                <div style="display: flex; flex-direction: row">
                    <div
                        :style="{
                            fontSize: props.isDailyPrint && '20px',
                            paddingLeft: props.isDailyPrint ? 0 : '0.1vw',
                        }"
                    >
                        독해력
                    </div>
                </div>
                <div
                    class="chart__text"
                    :style="{
                        fontSize: props.isDailyPrint && '35px',
                        lineHeight: props.isDailyPrint && '35px',
                    }"
                >
                    {{ props.label ? props.label : props.chartData }}<span class="chart__unit" :style="{ fontSize: props.isDailyPrint && '20px' }">{{ props.unit }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, defineProps } from 'vue';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import * as am5radar from '@amcharts/amcharts5/radar';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

const chartdiv = ref(null);
const props = defineProps({
    chartData: {
        default: 0,
    },
    endData: {
        default: 100,
    },
    header: {
        default: '',
    },
    unit: {
        default: '%',
    },
    label: {
        default: null,
    },
    isDailyPrint: {
        default: false,
    },
    isMonthlyPrint: {
        default: false,
    },
});
let root;
let chart;

onMounted(() => {
    root = am5.Root.new(chartdiv?.value);
    //2023-01-01 pby 재반영 확인
    root.setThemes([am5themes_Animated.new(root)]);
    //2023-01-01 pby 재반영 확인
    chart = root.container.children.push(
        am5radar.RadarChart.new(root, {
            panX: false,
            panY: false,
            startAngle: 180,
            endAngle: 360,
            paddingTop: -40,
            paddingBottom: -45,
            paddingLeft: -20,
            paddingRight: -20,
        }),
    );
    //2023-01-01 pby 재반영 확인
    chart.getNumberFormatter().set('numberFormat', '');

    var axisRenderer = am5radar.AxisRendererCircular.new(root, {
        innerRadius: am5.percent(70),
        outerRadius: 40,
    });
    //2023-01-01 pby 재반영 확인
    axisRenderer.grid.template.setAll({
        stroke: root.interfaceColors.get('background'),
        visible: false,
        strokeOpacity: 0.8,
    });
    //2023-01-01 pby 재반영 확인
    var xAxis = chart.xAxes.push(
        am5xy.ValueAxis.new(root, {
            maxDeviation: 0,
            min: 0,
            max: props.endData,
            strictMinMax: true,
            renderer: axisRenderer,
        }),
    );

    var axisDataItem = xAxis.makeDataItem({});

    var clockHand = am5radar.ClockHand.new(root, {
        // pinRadius: 80,
        radius: am5.percent(105),
        innerRadius: am5.percent(65),
        bottomWidth: 10,
        topWidth: 10,
    });

    clockHand.pin.setAll({
        fillOpacity: 0,
        strokeOpacity: 0,
        strokeWidth: 1,
        strokeDasharray: [2, 2],
    });
    clockHand.hand.setAll({
        fillOpacity: 1,
        strokeOpacity: 0.5,
        fill: '#18b73f',
        stroke: '#18b73f',
        strokeWidth: 0.5,
    });
    //2023-01-01 pby 재반영 확인
    xAxis.createAxisRange(axisDataItem);
    chart.radarContainer.children.push(
        am5.Label.new(root, {
            textAlign: 'center',
            centerX: am5.percent(50),
            centerY: am5.percent(70),
        }),
    );

    axisDataItem.set('value', 0);

    chart.bulletsContainer.set('mask', undefined);
    var axisRange1 = xAxis.createAxisRange(
        xAxis.makeDataItem({
            above: true,
            value: 0,
            endValue: props.endData,
        }),
    );
    //2023-01-01 pby 재반영 확인
    axisRange1.get('axisFill').setAll({
        visible: true,
        fill: '#EDEDED',
    });

    axisRange1.get('label').setAll({
        forceHidden: true,
    });

    var axisRange0 = xAxis.createAxisRange(
        xAxis.makeDataItem({
            above: true,
            value: 0,
            endValue: 0,
        }),
    );
    //2023-01-01 pby 재반영 확인
    axisRange0.get('axisFill').setAll({
        visible: true,
        fill: '#18b73f',
    });

    axisRange0.get('label').setAll({
        forceHidden: true,
    });

    setTimeout(() => {
        axisDataItem.animate({
            key: 'value',
            to: props.chartData,
            duration: 500,
            easing: am5.ease.out(am5.ease.cubic),
        });

        axisRange0.animate({
            key: 'endValue',
            to: props.chartData,
            duration: 500,
            easing: am5.ease.out(am5.ease.cubic),
        });
        //2023-01-01 pby 재반영 확인
        axisRange1.animate({
            key: 'value',
            to: props.chartData,
            duration: 500,
            easing: am5.ease.out(am5.ease.cubic),
        });
    }, 1000);
});

onUnmounted(() => {
    root.dispose();
    //2023-01-01 pby 재반영 확인
});
</script>

<style scoped lang="scss">
.chart {
    width: 100%;
    height: 100%;
    position: relative;

    &__wrap {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        display: inline-flex;
        align-items: end;
        justify-content: center;
    }

    &__area {
        width: 100%;
        height: 100%;
    }

    &__label {
        text-align: center;
        font-size: min(1.4vw, 28px);
    }

    &__text {
        font-size: min(3.3vw, 66px);
        line-height: min(3.3vw, 66px);
        font-weight: 700;
    }

    &__unit {
        font-size: 1.2vw;
        line-height: 1.6vw;
        font-weight: 500;
    }
}
.help__content {
    font-weight: 500;
    font-size: 1.1vw;
    line-height: 1.85vw;
    margin-left: 1.33vmin;
}
.question-mark {
    width: 2.1vw;
    height: 1.7vw;
    font-size: 1.5vw !important;
    align-items: flex-start;
}
.mark__list {
    li {
        font-size: 1.1vw;
        font-weight: 500;
        line-height: 1.85vw;
        word-break: keep-all;
        &:nth-of-type(n + 2) {
            margin-top: 0.67vmin;
        }
    }
}
@media print {
    .chart {
        ::v-deep > div {
            display: flex;
            justify-content: center;
        }
        ::v-deep canvas {
            width: 85% !important;
            height: 85% !important;
            top: 35% !important;
            right: 80% !important;
            transform: translate(-50%, -50%);
        }
    }
}
</style>
